import { sanitizeCenterTextInput } from "./centerTextRules";
import { setState } from "./configuratorStore";
import type {
  AnimationStyle,
  ConfiguratorState,
  DesignMode,
  FrameShape,
  LightStyle,
  SizeUnit,
} from "./types";
import type { CpbChangePayload } from "../cpbBridge";

type ChangeEntry = CpbChangePayload["changed"][number];
type Layer = CpbChangePayload["layers"][number];
export type RegistryHandler = (
  entry: ChangeEntry,
  layers: Layer[],
) => Partial<ConfiguratorState> | null;

/**
 * CPB schema registry.
 *
 * Update workflow when merchant changes CPB:
 * 1) Re-capture `cpbDiscovery.json`.
 * 2) Update `CAT` IDs below (primary mapping).
 * 3) If titles changed, update `fallbackAliases` only as needed.
 * 4) Keep handlers unchanged unless option semantics changed.
 */
export const CAT = {
  // STEP 1
  DESIGN_MODE: "rGBsXKwwThNiSwykhXb9lpED",
  LOGO_UPLOAD: "Qxp4cCZHBPSyjx8HBigw9Z0c",
  CENTER_TEXT: "SbHxRxgdGKf6gJA_Tm97efer",
  /** Step 1 — center design font only. */
  FONT_FAMILY: "isXiGLkqYyvCvXqsv4l0QLOX",
  /** Step 3 — perimeter / edge text font (“text lighting” along frame). */
  LED_TEXT_FONT_FAMILY: "DkvYo6CNoHB87aIPnRYEvu8v",
  FONT_SIZE: "kU4ohHIkyE-mXjYFoFLSscwb",
  FONT_COLOR: "JTXoUcEXaLD_BdJU2yNZQFyU",
  CENTER_TEXT_RGB: "9W7QZvQaz6Xx2CG7QP6DgP5Q",
  CENTER_LOGO_RGB: "PBO5FoiL7WyPoltf_adurqLd",
  CENTER_CONTRAST: "ewsqpkHYBWFEbxeuQx2MyANc",
  // STEP 2
  FRAME_SHAPE: "9Qos5yoFhBABVdL2vB4VBl0o",
  FRAME_COLOR: "HLDWufZPnynTp9Z5O_n85-_k",
  SIZE_MODE: "LB4oi7z56Ta0OZehKxWNh6o0",
  SIZE_PRESET_SQUARE: "Zs0mISKbbkrsanNymAIa6Zqf",
  SIZE_PRESET_RECT: "CQ5DSHGSBYKcjbeq1b8rf7MU",
  SIZE_UNIT: "SAvhX7xAkC66xUbZf1toKKOW",
  SIZE_CUSTOM_CM: "dbIF9OR5ZazT43lgZsJxGlfh",
  SIZE_CUSTOM_IN: "aE4hNil-ULOoL_nuXhFekRZl",
  // STEP 3
  LIGHT_STYLE: "-5rVa2YKJ10MFUHYFmKoRBJi",
  LIGHT_RGB_PRIMARY: "vcVfe5x2788HZFEUoWeDR3B",
  /** Frame LED RGB animation pattern. Only visible when frame `rgb === true`. */
  ANIMATION_STYLE: "WiiTVkyNoqTYLTdu3prfwrKO",
  /** Frame LED RGB color palette. Only visible when frame `rgb === true`. */
  RGB_PALETTE: "tX9plGZdKZe03MX-EEvGihNC",
  NEON_COLOR: "UrSKZIETH8x08xhpeuDYPDaQ",
  // Text Lighting sub-controls
  LED_TEXT: "4qoRqj0Fds_iRKAoRemupDzi",
  LED_TEXT_COLOR: "QIKsaieim4U8XmKkOQ26QBAt",
  LED_TEXT_RGB: "TtJNb2lF65yYG2-6EikO2-8a",
  LED_RAILS_ENABLED: "THbeDdkyGpIlF50ZEPquqtSS",
  LED_RAILS_RGB: "R7xYnCR-DaTjs-_skhviHb9b",
  LED_RAILS_COLOR: "gMkDYozZJBDN8GYrPVJS--NR",
  LED_RAILS_MODE: "JDRTFmz5m4UNfvh5S7lNMVxY",
} as const;

const FRAME_SHAPE_BY_LABEL: Record<string, FrameShape> = {
  square: "square",
  circle: "circle",
  landscape: "rectangle",
  portrait: "vrect",
  "oval - horizontal": "hoval",
  "oval - vertical": "voval",
};

const DESIGN_MODE_BY_LABEL: Record<string, DesignMode> = {
  "upload logo": "logo",
  text: "text",
  nothing: "nothing",
};

const LIGHT_STYLE_BY_LABEL: Record<string, LightStyle> = {
  "solid bar glow": "bar",
  "text lighting": "text",
  "point light": "point",
};

/**
 * Map CPB Animation Style option-IDs and human labels to `AnimationStyle`.
 * IDs come from the live CPB category `WiiTVkyNoqTYLTdu3prfwrKO`; the label
 * fallback covers re-keyed-by-CPB cases where the ID has rotated.
 */
const ANIMATION_STYLE_BY_OPTION_ID: Record<string, AnimationStyle> = {
  "sVS7DsVXL46YWU3U0s0m7uKu": "classic",
  "JIVma-0w_yhYpiqtRwqadQrj": "smoothFade",
  "lBL2a2hhXbn3e5Hej3pnx43K": "rapidChange",
  "8cGzkOGdM_v1FPIYWlrz6K9K": "chasing",
  "OGaiUv4fLCpdcUmnA4mIpe-9": "symmetrical",
  "NL6Hzk466neTkyQs0victk95": "pulsing",
  "bm9Dzeuxf6pVqTq46aaS7-YJ": "converging",
};

const ANIMATION_STYLE_BY_LABEL: Record<string, AnimationStyle> = {
  "classic": "classic",
  "smooth fade": "smoothFade",
  "rapid color change": "rapidChange",
  "chasing": "chasing",
  "symmetrical": "symmetrical",
  "pulsing": "pulsing",
  "converging": "converging",
};

/**
 * Map CPB "Choose Your RGB Color Palette" option-IDs to palette indices used
 * by `palettes.ts` / the LED LUT shader. Order in the CPB UI (left → right):
 * Choice 7 (default rainbow) is index 0; Choice 1..6 follow as indices 1..6.
 * The label fallback covers re-keyed option-IDs.
 */
const RGB_PALETTE_BY_OPTION_ID: Record<string, number> = {
  "XrlqnZLKPWIamZhjDOvCy4pX": 0, // Choice 7 — rainbow (default)
  "ym7cprJuTlqQoVgtGpBsJFfx": 1, // Choice 1
  "MhbMbyBIOZtLcyVl0EIj2oz0": 2, // Choice 2
  "39mjP3HbgRAo6xj4oscLAmMR": 3, // Choice 3
  "PslGdAgnlEGE2NeoIdysT9vw": 4, // Choice 4
  "avRt69hi1_CWp0ggWKe7jC76": 5, // Choice 5
  "SqT0Sou26f97PqalcEdS6LKb": 6, // Choice 6
};

const RGB_PALETTE_BY_LABEL: Record<string, number> = {
  "choice 7": 0,
  "choice 1": 1,
  "choice 2": 2,
  "choice 3": 3,
  "choice 4": 4,
  "choice 5": 5,
  "choice 6": 6,
};

const FRAME_COLOR_HEX_BY_LABEL: Record<string, string> = {
  black: "#111111",
  white: "#e8e8e8",
  gold: "#c9a227",
  chrome: "#a0a0a0",
  bronze: "#c4a882",
  "rose gold": "#b76e79",
};

function norm(s: string | undefined | null): string {
  return (s ?? "").trim().toLowerCase();
}

/**
 * CPB font dropdowns often expose:
 *  - `layerValue` — typed / persisted selection (can lag one tick behind the UI),
 *  - `value` — catalogue CSS stack (best for `canvas` / Three),
 *  - `label` — display name only.
 *
 * Prefer catalogue `value` over `layerValue` so the first click after a change
 * still maps correctly when CPB's layer snapshot hasn't flushed yet.
 */
function resolveCpbFontName(next: ChangeEntry["next"]): string | null {
  if (!next) return null;
  const catalogValue = typeof next.value === "string" ? next.value.trim() : "";
  const layerValue = typeof next.layerValue === "string" ? next.layerValue.trim() : "";
  const label = typeof next.label === "string" ? next.label.trim() : "";
  return catalogValue || layerValue || label || null;
}

/**
 * The font picker is a `react-select` dropdown. CPB's `CPB_ON_OPTIONS_UPDATE`
 * can fire before the visible `.singleValue` updates, so the DOM read alone
 * can still return the *previous* font on the first tick. Catalogue `value`
 * on `entry.next` (resolved from the new option id) is usually correct
 * immediately — `resolveCpbFontName` prefers that first.
 *
 * `readSelectedDropdownFont` remains a fallback when the resolved option has no
 * `value` / `label` in the catalogue payload.
 */
function readSelectedDropdownFont(categoryId: string): string | null {
  if (typeof document === "undefined") return null;
  const cat = document.querySelector(`[data-category-id="${categoryId}"]`);
  if (!cat) return null;
  const single = cat.querySelector('[class*="singleValue"]');
  const text = single?.textContent?.trim();
  return text && text.length > 0 ? text : null;
}

/**
 * Resolve the size preset for `shape` from the DOM and patch the store. CPB
 * renders the size-category swap asynchronously, so the target category may
 * not be in the DOM yet on the first attempt — retry over the next few frames.
 * Reads `.cpb-option.cpb-active` (synced with click) rather than
 * `.cpb-category-selected-choice` (lags by a frame).
 */
function applyPresetSizeFromDom(shape: FrameShape, attempt = 0): void {
  const sizeCatId =
    shape === "square" || shape === "circle"
      ? CAT.SIZE_PRESET_SQUARE
      : CAT.SIZE_PRESET_RECT;
  const label = document
    .querySelector(`[data-category-id="${sizeCatId}"] .cpb-option.cpb-active`)
    ?.firstElementChild?.textContent;
  const dims = parsePresetRectDims(label);
  if (dims) {
    setState({ size: dims.long, sizePresetShort: dims.short, sizeMode: "preset" });
    return;
  }
  if (attempt < 10) requestAnimationFrame(() => applyPresetSizeFromDom(shape, attempt + 1));
}

function parseHex(value: string | undefined): string | null {
  if (!value) return null;
  const m = value.match(/#([0-9a-fA-F]{6})/);
  return m ? `#${m[1].toLowerCase()}` : null;
}

function parseInt1(value: string | number | undefined | null): number | null {
  if (value == null) return null;
  const s = typeof value === "number" ? String(value) : value;
  const m = s.match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = Number.parseFloat(m[0]);
  return Number.isFinite(n) ? Math.round(n) : null;
}

/**
 * Pull the long/short side pair from a preset rect label such as
 * `"M – Classic (24" × 12")"` or `"3XL – Grand (72\" × 36\")"`. The dimensions
 * always sit at the end of the label, so we take the **last two** positive
 * numbers — that ignores leading size-name digits like the `3` in `3XL` and
 * keeps the proportion whatever CPB declares (no hardcoded 2:1 assumption).
 *
 * Falls back gracefully:
 *   - Single number (square preset, or value-only string) → { long: n, short: n }
 *   - No numbers / non-string                              → null
 */
function parsePresetRectDims(
  value: string | number | undefined | null,
): { long: number; short: number } | null {
  if (value == null) return null;
  const s = typeof value === "number" ? String(value) : value;
  const matches = s.match(/-?\d+(\.\d+)?/g);
  if (!matches || matches.length === 0) return null;
  const nums = matches
    .map((m) => Math.round(Number.parseFloat(m)))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (nums.length === 0) return null;
  if (nums.length === 1) return { long: nums[0], short: nums[0] };
  const a = nums[nums.length - 2];
  const b = nums[nums.length - 1];
  return { long: Math.max(a, b), short: Math.min(a, b) };
}

function layerValueAsString(layer: Layer | undefined): string | undefined {
  const v = layer?.value;
  if (v == null) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}

function resolveCustomSize(
  layers: Layer[],
  categoryId: string,
  unit: SizeUnit,
): Partial<ConfiguratorState> | null {
  const own = layers.filter((l) => l.category === categoryId);
  if (own.length === 0) return null;

  let width: number | null = null;
  let length: number | null = null;
  for (const layer of own) {
    const n = parseInt1(layerValueAsString(layer));
    if (n == null) continue;
    if (width == null) width = n;
    else if (length == null) length = n;
  }

  const patch: Partial<ConfiguratorState> = { sizeUnit: unit, sizeMode: "custom" };
  if (width != null) patch.sizeCustomWidth = width;
  if (length != null) patch.sizeCustomLength = length;
  return width != null || length != null ? patch : null;
}

const HANDLERS_BY_ID: Record<string, RegistryHandler> = {
  [CAT.DESIGN_MODE]: (entry) => {
    const mode = DESIGN_MODE_BY_LABEL[norm(entry.next?.label)];
    return mode ? { designMode: mode } : null;
  },
  [CAT.LOGO_UPLOAD]: (entry) => {
    const url = entry.next?.layerValue;
    if (!url) return { logoImageUrl: null, logoAssetRevision: "" };
    if (!/^https?:\/\//.test(url)) return null;
    return {
      logoImageUrl: url,
      designMode: "logo",
      logoAssetRevision: String(Date.now()),
    };
  },
  [CAT.CENTER_TEXT]: (entry) => {
    const text = entry.next?.layerValue;
    return text == null ? null : { centerText: sanitizeCenterTextInput(text) };
  },
  [CAT.FONT_FAMILY]: (entry) => {
    const name =
      resolveCpbFontName(entry.next) ?? readSelectedDropdownFont(CAT.FONT_FAMILY);
    return name ? { font: name } : null;
  },
  [CAT.LED_TEXT_FONT_FAMILY]: (entry) => {
    const fromResolved = resolveCpbFontName(entry.next);
    const fromDom = readSelectedDropdownFont(CAT.LED_TEXT_FONT_FAMILY);
    const name = fromResolved ?? fromDom;
    return name ? { ledTextFont: name } : null;
  },
  [CAT.FONT_SIZE]: (entry) => {
    const n = parseInt1(entry.next?.layerValue ?? entry.next?.label);
    return n != null ? { textFontSize: n } : null;
  },
  [CAT.FONT_COLOR]: (entry) => {
    // CPB moved RGB from a separate "Single Color or RGB" toggle into the color
    // picker itself — it's now one option among the 14 swatches, labelled "RGB"
    // and drawn as a black square. Selecting it flips `textRgb` on; selecting
    // any other swatch flips it off and updates `textColor`.
    if (norm(entry.next?.label) === "rgb") return { textRgb: true };
    const hex = parseHex(entry.next?.value) ?? parseHex(entry.next?.layerValue);
    return hex ? { textColor: hex, textRgb: false } : null;
  },
  // Step 1 — center design RGB toggles. Two distinct categories: one shown
  // when designMode === "text" (label "Single Color or RGB"), the other when
  // designMode === "logo" (label "Make Your Logo RGB?"). Each writes to its
  // own state slot — never to frame `rgb`.
  [CAT.CENTER_TEXT_RGB]: (entry) => {
    const v = parseRgbToggle(entry.next?.label);
    return v == null ? null : { textRgb: v };
  },
  [CAT.CENTER_LOGO_RGB]: (entry) => {
    const v = parseRgbToggle(entry.next?.label);
    return v == null ? null : { logoRgb: v };
  },
  [CAT.CENTER_CONTRAST]: (entry) => {
    const key = norm(entry.next?.label);
    if (key === "none" || key.startsWith("no")) return { centerContrast: "none" };
    if (key.startsWith("light") || key.startsWith("white")) return { centerContrast: "light" };
    if (key.startsWith("dark") || key.startsWith("black")) return { centerContrast: "dark" };
    return null;
  },
  [CAT.FRAME_SHAPE]: (entry) => {
    const shape = FRAME_SHAPE_BY_LABEL[norm(entry.next?.label)];
    if (!shape) return null;
    applyPresetSizeFromDom(shape);
    return { shape };
  },
  [CAT.FRAME_COLOR]: (entry) => {
    const hex = FRAME_COLOR_HEX_BY_LABEL[norm(entry.next?.label)];
    return hex ? { frameColor: hex } : null;
  },
  [CAT.SIZE_MODE]: (entry) => {
    const key = norm(entry.next?.label);
    if (key.includes("preset")) return { sizeMode: "preset" };
    if (key.includes("manual") || key.includes("custom")) return { sizeMode: "custom" };
    return null;
  },
  [CAT.SIZE_PRESET_SQUARE]: (entry) => {
    const n = parseInt1(entry.next?.layerValue ?? entry.next?.value ?? entry.next?.label);
    return n != null ? { size: n, sizePresetShort: n, sizeMode: "preset" } : null;
  },
  [CAT.SIZE_PRESET_RECT]: (entry) => {
    // Prefer the human-readable label — it carries both dimensions
    // ("24" × 12""). `value` / `layerValue` only hold the long side.
    const fromLabel = parsePresetRectDims(entry.next?.label);
    if (fromLabel) {
      return {
        size: fromLabel.long,
        sizePresetShort: fromLabel.short,
        sizeMode: "preset",
      };
    }
    const n = parseInt1(entry.next?.layerValue ?? entry.next?.value);
    return n != null
      ? { size: n, sizePresetShort: Math.max(1, Math.round(n / 2)), sizeMode: "preset" }
      : null;
  },
  [CAT.SIZE_UNIT]: (entry) => {
    const key = norm(entry.next?.label);
    if (key.startsWith("inch") || key === "in") return { sizeUnit: "in" };
    if (key.startsWith("cent") || key === "cm") return { sizeUnit: "cm" };
    return null;
  },
  [CAT.SIZE_CUSTOM_CM]: (_entry, layers) => resolveCustomSize(layers, CAT.SIZE_CUSTOM_CM, "cm"),
  [CAT.SIZE_CUSTOM_IN]: (_entry, layers) => resolveCustomSize(layers, CAT.SIZE_CUSTOM_IN, "in"),
  [CAT.LIGHT_STYLE]: (entry) => {
    const style = LIGHT_STYLE_BY_LABEL[norm(entry.next?.label)];
    return style ? { lightStyle: style } : null;
  },
  [CAT.LIGHT_RGB_PRIMARY]: (entry) => {
    const key = norm(entry.next?.label);
    if (key.startsWith("single")) return { rgb: false };
    if (key.startsWith("rgb")) return { rgb: true };
    return null;
  },
  [CAT.ANIMATION_STYLE]: (entry) => {
    const id = entry.next?.id;
    const byId = id ? ANIMATION_STYLE_BY_OPTION_ID[id] : undefined;
    if (byId) return { animationStyle: byId };
    const byLabel = ANIMATION_STYLE_BY_LABEL[norm(entry.next?.label)];
    return byLabel ? { animationStyle: byLabel } : null;
  },
  [CAT.RGB_PALETTE]: (entry) => {
    const id = entry.next?.id;
    if (id && id in RGB_PALETTE_BY_OPTION_ID) {
      return { rgbPalette: RGB_PALETTE_BY_OPTION_ID[id] };
    }
    const label = norm(entry.next?.label);
    if (label in RGB_PALETTE_BY_LABEL) {
      return { rgbPalette: RGB_PALETTE_BY_LABEL[label] };
    }
    return null;
  },
  // Side Lighting — Bar Glow / Point Light "Neon Color". If CPB still surfaces
  // the legacy category in parallel with the new merged LED_RAILS_COLOR
  // picker, RGB-as-color-option keeps frame `rgb` consistent.
  [CAT.NEON_COLOR]: (entry) => {
    if (norm(entry.next?.label) === "rgb") return { rgb: true };
    const hex = parseHex(entry.next?.value) ?? parseHex(entry.next?.layerValue);
    return hex ? { ledColor: hex, rgb: false } : null;
  },

  // Text Lighting — perimeter text input (single shared field for all shapes).
  [CAT.LED_TEXT]: (entry) => {
    const text = entry.next?.layerValue;
    return text != null ? { ledTextContent: text } : null;
  },

  // Text Lighting — text color (overrides ledColor for text path).
  // RGB-as-color-option: "RGB" swatch flips ledTextRgb on, any other swatch
  // flips it off and updates the solid hex.
  //
  // Cascade: CPB's UI hides the solid-color options on the "LED bars between
  // text" picker whenever the text colour is RGB — the bars are forced into
  // RGB as well. CPB doesn't always fire a separate LED_RAILS_COLOR event
  // when this auto-switch happens, so mirror the rule here: text RGB → rails
  // RGB. (Switching text back to a solid colour doesn't force rails back;
  // the user can pick any solid through the rails picker, which will fire
  // its own event and clear ledRailsRgb.)
  [CAT.LED_TEXT_COLOR]: (entry) => {
    if (norm(entry.next?.label) === "rgb") {
      return { ledTextRgb: true, ledRailsRgb: true };
    }
    const hex = parseHex(entry.next?.value) ?? parseHex(entry.next?.layerValue);
    return hex ? { ledTextColor: hex, ledTextRgb: false } : null;
  },

  // Text Lighting — RGB text toggle
  [CAT.LED_TEXT_RGB]: (entry) => {
    const key = norm(entry.next?.label);
    if (key === "no" || key.startsWith("no")) return { ledTextRgb: false };
    if (key === "yes" || key.startsWith("yes")) return { ledTextRgb: true };
    return null;
  },

  // Text Lighting — rails toggle
  //
  // Only flip the boolean. Earlier versions also reset `ledRailsRgb` and
  // `ledRailsColor` to their defaults whenever this handler saw a "yes"
  // label, which broke an interaction loop: switching lightStyle text →
  // point → text causes CPB to detach + re-attach the rails layers, and
  // the re-attach event fires this handler again — silently wiping any
  // RGB / color the user had picked. The dedicated `LED_RAILS_RGB` and
  // `LED_RAILS_COLOR` handlers below already manage those fields, so we
  // don't need to touch them here. First-ever enable still works because
  // `defaultConfiguratorState` initializes both to the same defaults.
  [CAT.LED_RAILS_ENABLED]: (entry) => {
    const key = norm(entry.next?.label);
    if (key === "no") return { ledRailsEnabled: false };
    if (key === "yes" || key.startsWith("yes")) return { ledRailsEnabled: true };
    return null;
  },

  // Text Lighting — rails RGB
  [CAT.LED_RAILS_RGB]: (entry) => {
    const key = norm(entry.next?.label);
    if (key.startsWith("single")) return { ledRailsRgb: false };
    if (key.startsWith("rgb")) return { ledRailsRgb: true };
    return null;
  },

  // Text Lighting / Side Lighting — "Choose your led bar color" (per the new
  // CPB merchant layout this single picker drives both Solid Bar Glow + Point
  // Light when in Side Lighting, and rails in Text Lighting). RGB swatch flips
  // ledRailsRgb on; other swatches flip it off and update the solid hex.
  [CAT.LED_RAILS_COLOR]: (entry) => {
    if (norm(entry.next?.label) === "rgb") return { ledRailsRgb: true };
    const hex = parseHex(entry.next?.value) ?? parseHex(entry.next?.layerValue);
    return hex ? { ledRailsColor: hex, ledRailsRgb: false } : null;
  },

  // Text Lighting — single vs double bars per depth band
  [CAT.LED_RAILS_MODE]: (entry) => {
    const key = norm(entry.next?.label);
    if (key.startsWith("single")) return { ledRailsMode: "single" };
    if (key.startsWith("double")) return { ledRailsMode: "double" };
    return null;
  },
};

type FallbackRule = {
  id: string;
  titleAliases: string[];
  labelAliases?: string[];
  /**
   * Restrict the rule to specific panels (CPB Step 1 / 2 / 3). Useful when the
   * SAME title (e.g. "Single Color or RGB") appears in multiple panels with
   * different semantics — Step 1 picker controls center design RGB, Step 3
   * picker controls frame LED RGB.
   */
  panelTitleAliases?: string[];
  handler: RegistryHandler;
};

/** Map a CPB toggle label ("No"/"Yes"/"Single Color"/"RGB") to a boolean. */
function parseRgbToggle(label: string | undefined | null): boolean | null {
  const key = norm(label);
  if (!key) return null;
  if (key.startsWith("single") || key === "no" || key.startsWith("no")) return false;
  if (key.startsWith("rgb") || key === "yes" || key.startsWith("yes")) return true;
  return null;
}

const FALLBACK_RULES: FallbackRule[] = [
  {
    id: "shape",
    titleAliases: ["shape"],
    handler: (entry) => {
      const shape = FRAME_SHAPE_BY_LABEL[norm(entry.next?.label)];
      return shape ? { shape } : null;
    },
  },
  {
    id: "frame-color",
    titleAliases: ["frame color", "choose your frame color"],
    handler: (entry) => {
      const hex = FRAME_COLOR_HEX_BY_LABEL[norm(entry.next?.label)];
      return hex ? { frameColor: hex } : null;
    },
  },
  {
    id: "font-color",
    titleAliases: ["font color", "text color"],
    handler: (entry) => {
      if (norm(entry.next?.label) === "rgb") return { textRgb: true };
      const hex = parseHex(entry.next?.value) ?? parseHex(entry.next?.layerValue);
      return hex ? { textColor: hex, textRgb: false } : null;
    },
  },
  {
    id: "led-color",
    titleAliases: ["neon color", "led color", "choose your led bar color"],
    handler: (entry) => {
      if (norm(entry.next?.label) === "rgb") return { rgb: true };
      const hex = parseHex(entry.next?.value) ?? parseHex(entry.next?.layerValue);
      return hex ? { ledColor: hex, rgb: false } : null;
    },
  },
  // Step 1 — center logo RGB ("Make Your Logo RGB?" or any title with logo+rgb).
  // Visible only when designMode === "logo".
  {
    id: "center-logo-rgb-toggle",
    titleAliases: ["make your logo rgb", "logo rgb"],
    labelAliases: ["single", "rgb", "yes", "no"],
    panelTitleAliases: ["step 1", "design", "middle"],
    handler: (entry) => {
      const v = parseRgbToggle(entry.next?.label);
      return v == null ? null : { logoRgb: v };
    },
  },
  // Step 1 — center text RGB. CPB shows "Single Color or RGB" or "RGB Text?"
  // inside Step 1 when designMode === "text"; the toggle controls the center
  // design's RGB cycle, NOT the frame LED.
  {
    id: "center-text-rgb-toggle",
    titleAliases: ["single color or rgb", "rgb text?", "rgb text"],
    labelAliases: ["single", "rgb", "yes", "no"],
    panelTitleAliases: ["step 1", "design", "middle"],
    handler: (entry) => {
      const v = parseRgbToggle(entry.next?.label);
      return v == null ? null : { textRgb: v };
    },
  },
  // Step 3 — frame LED RGB. Same "Single Color or RGB" title, but in the
  // lighting panel it drives the perimeter LED rgb cycle.
  {
    id: "frame-rgb-toggle",
    titleAliases: ["single color or rgb"],
    labelAliases: ["single", "rgb", "yes", "no"],
    panelTitleAliases: ["step 3", "lighting", "side"],
    handler: (entry) => {
      const v = parseRgbToggle(entry.next?.label);
      return v == null ? null : { rgb: v };
    },
  },
];

function includesAlias(value: string, aliases: string[]): boolean {
  if (!value) return false;
  return aliases.some((alias) => value.includes(alias));
}

export type RegistryResolveResult =
  | { kind: "id"; handler: RegistryHandler; matchedBy: string }
  | { kind: "fallback"; handler: RegistryHandler; matchedBy: string }
  | { kind: "none" };

export function resolveRegistryHandler(entry: ChangeEntry): RegistryResolveResult {
  const byId = HANDLERS_BY_ID[entry.category.id];
  if (byId) return { kind: "id", handler: byId, matchedBy: entry.category.id };

  const title = norm(entry.category.title);
  const panelTitle = norm(entry.category.panelTitle);
  const label = norm(entry.next?.label);
  for (const rule of FALLBACK_RULES) {
    if (!includesAlias(title, rule.titleAliases)) continue;
    if (rule.panelTitleAliases && !includesAlias(panelTitle, rule.panelTitleAliases)) continue;
    if (rule.labelAliases && label && !includesAlias(label, rule.labelAliases)) continue;
    return { kind: "fallback", handler: rule.handler, matchedBy: rule.id };
  }

  return { kind: "none" };
}

